import { useState } from "react";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount";
import UnloadHandler from "@components/unload_handler";
import { Body, SectionHeader } from "@sprout/typography";
import { Checkbox } from "@sprout/checkbox";
import { Location } from "@components/ui/location_input";
import { useShowFlash } from "@sprout/flash.tsx";
import { useTranslation } from "react-i18next";
import Container from "@components/ui/container.tsx";
import BackButton from "@components/back_button.tsx";
import "./work_preferences.scss";
import { LocationInput } from "@components/ui/location_input.tsx";
import { AsyncMultiSelect, MultiSelect, type Option } from "@sprout/select";
import { useLoadAsyncOptions } from "@sprout/select/hooks";
import useGoBack from "@/hooks/use_go_back";
import axios, { isAxiosError } from "axios";
import { PrimaryButton, SecondaryButton } from "@sprout/buttons";

type Props = {
  employmentTypes: Option[];
  worksites: Option[];
  currentLanguages: Option[];
  currentEmploymentTypes: Option[];
  currentWorksites: Option[];
  currentRelocation: boolean | null;
  currentLocation: Location | null;
  paths: {
    workPreferencesPath: string;
  };
};

const WorkPreferences = ({
  currentEmploymentTypes,
  currentLanguages,
  currentLocation,
  currentRelocation,
  currentWorksites,
  paths,
  ...props
}: Props) => {
  const showFlash = useShowFlash();

  const [languages, setLanguages] = useState<Option[]>(currentLanguages);
  const loadLanguageOptions = useLoadAsyncOptions("/languages");
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(currentLocation);
  const [employmentTypes, setEmploymentTypes] = useState<Option[]>(currentEmploymentTypes);
  const [worksites, setWorksites] = useState<Option[]>(currentWorksites);
  const [relocation, setRelocation] = useState<boolean | null>(currentRelocation);
  const [relocationError, setRelocationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const { t } = useTranslation("user");
  const goBack = useGoBack();

  useEffectAfterMount(() => {
    setUnsavedChanges(true);
  }, [languages, employmentTypes, worksites, relocation, location, relocationError, error]);

  const submit = async () => {
    setLoading(true);
    setError(null);
    setLanguageError(null);
    setRelocationError(null);

    try {
      const formData = new FormData();

      languages.forEach((language) => {
        formData.append("languages[][id]", language.value.toString());
      });

      employmentTypes.forEach((employmentType) => {
        formData.append("employment_types[][category]", employmentType.value.toString());
      });

      worksites.forEach((worksite) => {
        formData.append("worksites[][category]", worksite.value.toString());
      });

      formData.append("preference[relocation]", relocation?.toString() || "");
      formData.append("preference[location]", JSON.stringify(location));

      await axios.patch(paths.workPreferencesPath, formData);

      showFlash(t("workPreferences.flashSuccess"), "success");
      setUnsavedChanges(false);
      goBack();
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 422) {
        const { errors } = e.response.data;

        if (errors.user_language) {
          setLanguageError(errors.user_language[0]);
        }

        if (errors.relocation) {
          setRelocationError(errors.relocation[0]);
        }
      }

      console.error(e);
      setError(t("workPreferences.flashError"));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLanguages = (selectedLanguages: Option[]) => {
    if (selectedLanguages.length > 5) {
      return;
    }

    setLanguages(selectedLanguages);
  };

  return (
    <Container>
      {unsavedChanges && <UnloadHandler />}
      <BackButton href="/profile" />
      <div className="flex flex-col gap-[4px]">
        <SectionHeader as="h1">{t("workPreferences.header")}</SectionHeader>
        <Body>{t("workPreferences.description")}</Body>
      </div>
      <div className="work-preference-inputs">
        <AsyncMultiSelect
          onChange={handleUpdateLanguages}
          values={languages}
          label={t("workPreferences.languages.label")}
          helperText={t("workPreferences.languages.helperText")}
          error={languageError}
          clearable
          loadOptions={loadLanguageOptions}
        />
        <MultiSelect
          onChange={setEmploymentTypes}
          options={props.employmentTypes}
          values={employmentTypes}
          label={t("workPreferences.employmentTypes.label")}
          clearable
        />
        <MultiSelect
          clearable
          onChange={setWorksites}
          options={props.worksites}
          values={worksites}
          label={t("workPreferences.worksites.label")}
        />
        <LocationInput
          value={location}
          onChange={setLocation}
          helperText={t("workPreferences.locationHelperText")}
        />
        <Checkbox
          id="relocation"
          options={[{ value: "1", label: t("workPreferences.willingToRelocate") }]}
          onSelect={() => setRelocation(!relocation)}
          values={relocation ? ["1"] : []}
          error={relocationError}
          isMulti
        />
      </div>

      <div className="submit-buttons-stack">
        <PrimaryButton onClick={submit} loading={loading}>
          {t("workPreferences.save")}
        </PrimaryButton>
        <SecondaryButton onClick={goBack}>{t("workPreferences.cancel")}</SecondaryButton>
        {error && (
          <p
            id="work-preferences-error"
            className="helper-text helper-text--error"
            aria-live="polite"
          >
            {error}
          </p>
        )}
      </div>
    </Container>
  );
};

export default WorkPreferences;
